// Promotions (Akcijas) — manages product sale prices.
//
// There is no separate campaign table: a promotion is simply a product with a
// `sale_price` set (lower than its base price). The storefront's /category/akcijas
// view lists exactly these. This page is the at-a-glance manager for them —
// see every discounted product, edit or clear the sale price inline, and add a
// new one via search — without opening the full product editor.

import { useCallback, useEffect, useRef, useState } from "react";
import { Percent, Search, Trash2, Check, Loader2 } from "lucide-react";
import { supabase } from "@vinoga/db";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

type Row = {
  id: string;
  sku: string | null;
  name: string;
  base: number | null;
  sale: string; // editable string
};

function nameOf(translations: any, sku: string | null): string {
  return translations?.lv?.name ?? translations?.en?.name ?? sku ?? "—";
}

function pct(base: number | null, sale: number | null): string {
  if (base == null || sale == null || base <= 0 || sale >= base) return "";
  return `−${Math.round((1 - sale / base) * 100)}%`;
}

export function Promotions() {
  const { toast } = useToast();
  const tr = useT();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [baseListId, setBaseListId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: pl } = await supabase
      .from("price_lists")
      .select("id")
      .eq("code", "base")
      .single();
    const basePriceListId = pl?.id ?? null;
    setBaseListId(basePriceListId);

    const { data: prods } = await supabase
      .from("products")
      .select("id, sku, translations, sale_price")
      .not("sale_price", "is", null)
      .order("updated_at", { ascending: false });
    const list = prods ?? [];
    const ids = list.map((p: any) => p.id);

    const baseMap: Record<string, number> = {};
    if (basePriceListId && ids.length) {
      const { data: prices } = await supabase
        .from("product_prices")
        .select("product_id, amount")
        .eq("price_list_id", basePriceListId)
        .in("product_id", ids);
      (prices ?? []).forEach((r: any) => (baseMap[r.product_id] = Number(r.amount)));
    }

    setRows(
      list.map((p: any) => ({
        id: p.id,
        sku: p.sku,
        name: nameOf(p.translations, p.sku),
        base: baseMap[p.id] ?? null,
        sale: p.sale_price != null ? String(p.sale_price) : "",
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveSale = useCallback(
    async (id: string, sale: string) => {
      setSavingId(id);
      const value = sale.trim() === "" ? null : sale.trim();
      const { error } = await supabase
        .from("products")
        .update({ sale_price: value })
        .eq("id", id);
      setSavingId(null);
      if (error) {
        toast({ title: tr("common.saveFailed", "Save failed"), variant: "destructive" });
        return;
      }
      toast({ title: tr("common.saved", "Saved") });
      // Clearing the sale removes the row from the list.
      if (value == null) setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [toast, tr],
  );

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Percent size={20} className="text-primary" />
            {tr("promotions.title", "Promotions")}
          </h1>
          <p className="mt-1 font-body text-sm text-muted-foreground">
            {tr(
              "promotions.subtitle",
              "Products with an active sale price. These appear under Akcijas on the storefront.",
            )}
          </p>
        </div>
      </div>

      <AddPromotion baseListId={baseListId} onAdded={load} />

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> {tr("common.loading", "Loading…")}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-10 font-body text-sm text-muted-foreground">
          {tr("promotions.empty", "No products are on sale yet.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">{tr("promotions.colProduct", "Product")}</th>
                <th className="px-4 py-3 text-right">{tr("promotions.colBase", "Base")}</th>
                <th className="px-4 py-3 text-right">{tr("promotions.colSale", "Sale price")}</th>
                <th className="px-4 py-3 text-right">{tr("promotions.colOff", "Off")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{r.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{r.sku}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-muted-foreground">
                    {r.base != null ? `€${r.base.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={r.sale}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x) => (x.id === r.id ? { ...x, sale: e.target.value } : x)),
                        )
                      }
                      className="w-24 rounded border border-border bg-card px-2 py-1 text-right"
                    />
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-primary">
                    {pct(r.base, r.sale ? Number(r.sale) : null)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={savingId === r.id}
                        onClick={() => saveSale(r.id, r.sale)}
                      >
                        {savingId === r.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <Check size={14} />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title={tr("promotions.remove", "Remove sale")}
                        onClick={() => saveSale(r.id, "")}
                      >
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Search for a product and give it a sale price. */
function AddPromotion({
  baseListId,
  onAdded,
}: {
  baseListId: string | null;
  onAdded: () => void;
}) {
  const { toast } = useToast();
  const tr = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<
    Array<{ id: string; sku: string | null; name: string; base: number | null }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<{ id: string; name: string; base: number | null } | null>(
    null,
  );
  const [sale, setSale] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { ids } = await api<{ ids: string[] }>(
          `/api/v1/admin/products/search?q=${encodeURIComponent(q.trim())}&limit=8`,
        );
        const top = ids.slice(0, 8);
        if (top.length === 0) {
          setResults([]);
          return;
        }
        const { data: prods } = await supabase
          .from("products")
          .select("id, sku, translations")
          .in("id", top);
        const baseMap: Record<string, number> = {};
        if (baseListId) {
          const { data: prices } = await supabase
            .from("product_prices")
            .select("product_id, amount")
            .eq("price_list_id", baseListId)
            .in("product_id", top);
          (prices ?? []).forEach((r: any) => (baseMap[r.product_id] = Number(r.amount)));
        }
        setResults(
          (prods ?? []).map((p: any) => ({
            id: p.id,
            sku: p.sku,
            name: nameOf(p.translations, p.sku),
            base: baseMap[p.id] ?? null,
          })),
        );
      } finally {
        setSearching(false);
      }
    }, 250);
  }, [q, baseListId]);

  const apply = useCallback(async () => {
    if (!picked || sale.trim() === "") return;
    const { error } = await supabase
      .from("products")
      .update({ sale_price: sale.trim() })
      .eq("id", picked.id);
    if (error) {
      toast({ title: tr("common.saveFailed", "Save failed"), variant: "destructive" });
      return;
    }
    toast({ title: tr("promotions.added", "Sale added") });
    setPicked(null);
    setSale("");
    setQ("");
    setResults([]);
    onAdded();
  }, [picked, sale, toast, tr, onAdded]);

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-4">
      <div className="mb-2 font-body text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {tr("promotions.add", "Add a sale")}
      </div>
      {picked ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <div className="font-medium text-foreground">{picked.name}</div>
            <div className="text-xs text-muted-foreground">
              {tr("promotions.colBase", "Base")}:{" "}
              {picked.base != null ? `€${picked.base.toFixed(2)}` : "—"}
            </div>
          </div>
          <input
            type="number"
            step="0.01"
            autoFocus
            placeholder={tr("promotions.salePlaceholder", "Sale price")}
            value={sale}
            onChange={(e) => setSale(e.target.value)}
            className="w-32 rounded border border-border bg-card px-2 py-1.5 text-right"
          />
          <Button size="sm" onClick={apply} disabled={sale.trim() === ""}>
            {tr("common.save", "Save")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setPicked(null);
              setSale("");
            }}
          >
            {tr("common.cancel", "Cancel")}
          </Button>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-2 rounded border border-border bg-card px-3">
            <Search size={16} className="text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr("promotions.searchPlaceholder", "Search a product to put on sale…")}
              className="w-full bg-transparent py-2 text-sm outline-none"
            />
            {searching && <Loader2 className="animate-spin text-muted-foreground" size={14} />}
          </div>
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-border bg-card shadow-lg">
              {results.map((r) => (
                <button
                  key={r.id}
                  onClick={() => {
                    setPicked({ id: r.id, name: r.name, base: r.base });
                    setResults([]);
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/40"
                >
                  <span>
                    <span className="font-medium text-foreground">{r.name}</span>{" "}
                    <span className="font-mono text-xs text-muted-foreground">{r.sku}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.base != null ? `€${r.base.toFixed(2)}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
